/**
 * Movement in the Pictures view (spec 035): for a character expression whose
 * picture is a spritesheet, Maya's plain controls (speed, Keep repeating,
 * Make it still / Make it move), each applied to the expression everywhere
 * it is used through one `setPicturePlayback` step (D-4); for one whose
 * picture is a still, the way to add movement (034's dialog, one animation,
 * D-5). The panel says "expression", never "state" or "costume".
 */
import React from 'react';
import type { PictureSlot, StatePlayback } from '../../../../Shared/src/@types';
import PictureThumb, { type PictureThumbProps } from './PictureThumb';
import { usePicture } from '../sheets/usePicture';
import { useSheetClock } from '../sheets/useSheetClock';
import { slotStateUses } from '../../../../Shared/src/storyOperations';
import { DEFAULT_PLAYBACK } from '../../../../Shared/src/utils/spriteSheet';
import { useMediaSheetStore } from '../../stores/mediaSheetStore';
import SpeedSlider from '../sheets/SpeedSlider';
import { withMode, withSpeed } from '../sheets/PlaybackControls';
import type { PictureSceneRow } from './picturesModel';
import styles from './Pictures.module.scss';

export const MOVEMENT_TEXT = {
  keepRepeating: 'Keep repeating',
  makeStill: 'Make it still',
  makeMove: 'Make it move',
  replace: 'Replace with a spritesheet…',
  addMovement: 'Add movement · Import a spritesheet…',
  applies: (label: string, n: number, differ: boolean) =>
    `Applies to ${label} in every scene (${n} ${n === 1 ? 'place' : 'places'}${differ ? ', now set differently' : ''})`,
  failed: "This change couldn't be applied. Try again.",
} as const;

/** The playback each use of the slot has now, in use order (absent reads as the default loop). */
export function slotPlaybacks(slot: PictureSlot, scenes: readonly PictureSceneRow[]): StatePlayback[] {
  return slotStateUses(slot).map((use) => {
    const scene = scenes.find((s) => s.key === use.scene);
    const sprite = scene?.doc.sprites?.find((sp) => sp.id === use.spriteId);
    const state = sprite?.states?.find((st) => st.name === use.state);
    return state?.playback ?? DEFAULT_PLAYBACK;
  });
}

/**
 * A slot's thumbnail: a spritesheet shows its first frame and "MOVES" (spec 035).
 * It reads the cut itself, so a sheet this session did not import (made
 * through the API) shows too.
 * Given `playback` (the panel preview: the first use's playback), the badge is
 * hidden and a sheet that isn't Still gets a Play/Pause button instead, which
 * plays it on the shared sheet clock (`useSheetClock`): it starts paused on
 * frame 0 and stays disabled until the image has loaded; Pause then Play
 * resumes; a change of playback while playing applies at once and keeps the
 * phase; Once stops at its end (per `whenDone`) and turns the button back to
 * Play, and the next Play starts over.
 */
export const SlotThumb: React.FC<Omit<PictureThumbProps, 'sheet' | 'overlay' | 'badge'> & { mediaId?: number; playback?: StatePlayback }> = ({
  mediaId, playback, ...props
}) => {
  const cut = useMediaSheetStore((s) => (mediaId ? s.sheets[mediaId] : undefined));
  // Read the cut here: a sheet not imported in this session (e.g. made through the API) is not in the store yet.
  React.useEffect(() => { if (mediaId) void useMediaSheetStore.getState().loadSheets([mediaId]); }, [mediaId]);
  const picture = usePicture(cut ? mediaId : undefined);
  const panel = !!playback;
  const playable = !!cut && panel && playback.mode !== 'still' && props.filled;
  const ready = !!picture.natural;
  const [playing, setPlaying] = React.useState(false);
  const [frame, setFrame] = React.useState(0);
  React.useEffect(() => { if (!playable) { setPlaying(false); setFrame(0); } }, [playable]);
  useSheetClock(cut, playback, playing && playable && ready, `slot-${mediaId ?? 0}`, (at) => {
    setFrame(at.index);
    if (at.done) setPlaying(false);
  });
  const overlay = playable ? (
    <button
      type="button"
      className={styles.previewPlay}
      aria-label={playing ? 'Pause' : 'Play'}
      disabled={!ready}
      onClick={(e) => { e.stopPropagation(); setPlaying((p) => !p); }}
      data-testid="picture-preview-play"
    >
      {playing ? '❚❚' : '▶'}
    </button>
  ) : undefined;
  return <PictureThumb {...props} sheet={cut ? { cut, natural: picture.natural, frame } : undefined} overlay={overlay} badge={!panel} />;
};

export interface PictureMovementProps {
  slot: PictureSlot;
  label: string;
  scenes: readonly PictureSceneRow[];
  busy: boolean;
  /** The playback the controls show: the value just set until the scenes agree (optimistic), else the first use's. */
  shown?: StatePlayback;
  /** One `setPicturePlayback` step through the owner; resolves with a failure's text, or null. */
  apply: (playback: StatePlayback) => Promise<string | null>;
  /** 034's dialog in expression mode. */
  onImport: () => void;
}

const PictureMovement: React.FC<PictureMovementProps> = ({ slot, label, scenes, busy, shown, apply, onImport }) => {
  const cut = useMediaSheetStore((s) => (slot.mediaId ? s.sheets[slot.mediaId] : undefined));
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => { setError(null); }, [slot.id]);
  if (slot.kind !== 'character') return null;
  if (!cut) {
    return (
      <div className={styles.movement} data-testid="pictures-movement">
        <button type="button" className={styles.link} onClick={onImport} data-testid="pictures-add-movement">{MOVEMENT_TEXT.addMovement}</button>
      </div>
    );
  }
  const playbacks = slotPlaybacks(slot, scenes);
  const first = shown ?? playbacks[0] ?? DEFAULT_PLAYBACK;
  const differ = playbacks.some((p) => JSON.stringify(p) !== JSON.stringify(playbacks[0]));
  const still = first.mode === 'still';
  const run = async (next: StatePlayback) => {
    setError(await apply(next));
  };

  return (
    <div className={styles.movement} data-testid="pictures-movement">
      <SpeedSlider variant="plain" speed={first.speed} disabled={busy || still} onChange={(v) => void run(withSpeed(first, v))} testId="pictures-speed" />
      <label>
        <input
          type="checkbox"
          checked={first.mode === 'loop' || first.mode === 'pingpong'}
          disabled={busy || still}
          onChange={(e) => void run(withMode(first, e.target.checked ? 'loop' : 'once'))}
          data-testid="pictures-keep-repeating"
        />{' '}
        {MOVEMENT_TEXT.keepRepeating}
      </label>
      <p className={styles.movementApplies} data-testid="pictures-movement-applies">{MOVEMENT_TEXT.applies(label, playbacks.length, differ)}</p>
      <div>
        <button type="button" className={styles.link} disabled={busy} onClick={onImport} data-testid="pictures-replace-sheet">{MOVEMENT_TEXT.replace}</button>
        {' · '}
        <button
          type="button"
          className={styles.link}
          disabled={busy}
          onClick={() => void run(withMode(first, still ? 'loop' : 'still'))}
          data-testid="pictures-still"
        >
          {still ? MOVEMENT_TEXT.makeMove : MOVEMENT_TEXT.makeStill}
        </button>
      </div>
      {error && <p className={styles.error} role="alert" data-testid="pictures-movement-error">{error}</p>}
    </div>
  );
};

export default PictureMovement;
